import axios from 'axios';
import i18next from 'i18next';

export const detectMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * 统一的音乐分享工具函数
 * @param {number} musicId - 音乐ID
 * @param {string} currentLang - 当前语言（从 i18n.language 获取）
 * @param {Function} onSuccess - 分享成功回调（可选，用于更新分享计数）
 * @returns {Promise<void>}
 */
export const shareMusic = async (musicId, currentLang = 'cn', onSuccess = null, options = {}) => {
  try {
    // 调用API获取分享信息
    const response = await axios.get(`/api/music/${musicId}/share`, {
      params: { lang: currentLang },
      headers: { 'Referer': window.location.href }
    });
    
    if (response.data.success) {
      const shareInfo = response.data.data;
      const shareText = `${shareInfo.title}${shareInfo.artist ? ' - ' + shareInfo.artist : ''}\n${shareInfo.url}`;
      
      // 检测是否为移动设备（Web Share API 主要在移动设备上支持）
      const isMobile = detectMobileDevice();
      const autoCopyDesktop = options?.autoCopyDesktop === true;
      let usedWebShare = false;
      let copied = false;
      
      // 检查是否可以使用 Web Share API（仅在移动设备上，且需要 HTTPS 或 localhost）
      const canUseWebShare = isMobile && 
        navigator.share && 
        typeof navigator.share === 'function' &&
        (window.location.protocol === 'https:' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      
      if (canUseWebShare) {
        try {
          // 检查是否支持分享（某些浏览器可能不支持）
          if (navigator.canShare && !navigator.canShare({ title: shareInfo.title, text: shareText, url: shareInfo.url })) {
            // 不支持分享，降级到复制（只复制链接）
            copyToClipboard(shareInfo.url, () => {
              if (options?.onCopyHint) options.onCopyHint();
            });
            return;
          }
          
          await navigator.share({
            title: shareInfo.title,
            text: shareText,
            url: shareInfo.url
          });
          usedWebShare = true;
          
          // 分享成功，调用回调函数（用于更新分享计数）
          if (onSuccess && typeof onSuccess === 'function') {
            onSuccess();
          }
          return { shareInfo, isMobile, usedWebShare, copied };
        } catch (shareError) {
          // 用户取消分享（AbortError）或其他错误，回退到复制
          if (shareError.name === 'AbortError') {
            // 用户取消，不执行任何操作
            return { shareInfo, isMobile, usedWebShare, copied };
          } else {
            // 其他错误（如"无法显示所有共享方法"），降级到复制（只复制链接）
            console.log('Web Share API 失败，降级到复制链接:', shareError);
            copyToClipboard(shareInfo.url, () => {
              copied = true;
              if (options?.onCopyHint) options.onCopyHint();
              if (onSuccess && typeof onSuccess === 'function') {
                onSuccess();
              }
            });
            return { shareInfo, isMobile, usedWebShare, copied };
          }
        }
      } else if (isMobile || autoCopyDesktop) {
        // 移动端或主动要求：复制到剪贴板（只复制链接）
        copyToClipboard(shareInfo.url, () => {
          copied = true;
          if (options?.onCopyHint) options.onCopyHint();
          if (onSuccess && typeof onSuccess === 'function') {
            onSuccess();
          }
        });
        return { shareInfo, isMobile, usedWebShare, copied };
      }
      return { shareInfo, isMobile, usedWebShare, copied };
    }
  } catch (error) {
    console.error('Share error:', error);
    alert(i18next.t('hints.shareFailed'));
  }
};

/**
 * 复制到剪贴板（只复制链接地址，不弹窗）
 * @param {string} url - 要复制的URL
 * @param {Function} onSuccess - 复制成功回调（可选）
 */
export const copyToClipboard = (url, onSuccess = null) => {
  // 优先使用 Clipboard API（现代浏览器）
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(url).then(() => {
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess();
      }
    }).catch(err => {
      console.error('Clipboard API 失败，使用降级方案:', err);
      fallbackCopyToClipboard(url, onSuccess);
    });
  } else {
    // 降级方案（兼容 IE 和旧版浏览器）
    fallbackCopyToClipboard(url, onSuccess);
  }
};

/**
 * 降级复制方案（兼容 IE 和旧版浏览器）
 * @param {string} url - 要复制的URL
 * @param {Function} onSuccess - 复制成功回调（可选）
 */
const fallbackCopyToClipboard = (url, onSuccess = null) => {
  try {
    const textarea = document.createElement('textarea');
    textarea.value = url;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textarea);
    if (successful && onSuccess && typeof onSuccess === 'function') {
      onSuccess();
    }
  } catch (err) {
    console.error('复制失败:', err);
    if (onSuccess && typeof onSuccess === 'function') {
      onSuccess();
    }
  }
};

